import React, { useState, useEffect, useCallback } from 'react';
import { Building2, ChevronDown, Search, Check, Plus, Loader2, Users } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { cn } from '../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AccountSwitcher({ className }) {
  const { user, token } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]); // For admin view
  const [activeAccount, setActiveAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState('my'); // 'my' or 'all' for admin

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.email === 'oneforceticketing@gmail.com';

  // Fetch accounts on mount
  const fetchAccounts = useCallback(async () => {
    if (!token) return;
    
    try {
      // Fetch user's own accounts
      const response = await fetch(`${API_URL}/api/accounts/my-accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAccounts(data.accounts || []);
        // Find active account
        const active = data.accounts?.find(a => a.is_active);
        setActiveAccount(active || data.accounts?.[0]);
      }

      // If admin, also fetch all client accounts
      if (isAdmin) {
        const allResponse = await fetch(`${API_URL}/api/accounts/all-clients`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (allResponse.ok) {
          const allData = await allResponse.json();
          setAllAccounts(allData.accounts || []);
        }
      }
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    } finally {
      setLoading(false);
    }
  }, [token, isAdmin]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleSwitchAccount = async (account) => {
    if (account.id === activeAccount?.id) {
      setIsOpen(false);
      return;
    }
    
    setSwitching(true);
    
    try {
      const response = await fetch(`${API_URL}/api/accounts/switch`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ account_id: account.id })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Store new token
        localStorage.setItem('token', data.access_token);
        
        // Update active account
        setActiveAccount(data.active_account);
        
        toast.success(`Switched to ${data.active_account.name || data.active_account.business_name}`);
        
        // Reload the page to refresh all data for the new account context
        setTimeout(() => {
          window.location.reload();
        }, 500);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to switch account');
      }
    } catch (error) {
      console.error('Failed to switch account:', error);
      toast.error('Failed to switch account');
    } finally {
      setSwitching(false);
      setIsOpen(false);
    }
  };

  // Get the list to display based on view mode
  const displayAccounts = viewMode === 'all' && isAdmin ? allAccounts : accounts;

  // Filter accounts by search
  const filteredAccounts = displayAccounts.filter(account => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      account.name?.toLowerCase().includes(query) ||
      account.business_name?.toLowerCase().includes(query) ||
      account.location?.toLowerCase().includes(query) ||
      account.owner_name?.toLowerCase().includes(query) ||
      account.owner_email?.toLowerCase().includes(query)
    );
  });

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger
        className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-all ml-2",
          className
        )}
        data-testid="account-switcher"
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        ) : (
          <>
            <div className="hidden sm:block text-left max-w-[200px]">
              <p className="text-xs font-medium text-white truncate">
                {activeAccount?.business_name || activeAccount?.name || user?.name || 'My Account'}
              </p>
              {activeAccount?.location ? (
                <p className="text-[10px] text-gray-400 truncate">{activeAccount.location}</p>
              ) : (
                <p className="text-[10px] text-gray-400">Switch Account</p>
              )}
            </div>
            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
          </>
        )}
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        align="start" 
        className="bg-[#12121a] border-white/10 w-80 max-h-[450px] overflow-hidden"
        data-testid="account-switcher-menu"
      >
        {/* Admin Toggle - My Accounts vs All Clients */}
        {isAdmin && (
          <div className="p-2 border-b border-white/10">
            <div className="flex rounded-lg bg-white/5 p-1">
              <button
                onClick={() => setViewMode('my')}
                className={cn(
                  "flex-1 py-1.5 px-3 rounded-md text-xs font-medium transition-colors",
                  viewMode === 'my' 
                    ? "bg-primary text-black" 
                    : "text-gray-400 hover:text-white"
                )}
              >
                My Accounts
              </button>
              <button
                onClick={() => setViewMode('all')}
                className={cn(
                  "flex-1 py-1.5 px-3 rounded-md text-xs font-medium transition-colors flex items-center justify-center gap-1",
                  viewMode === 'all' 
                    ? "bg-primary text-black" 
                    : "text-gray-400 hover:text-white"
                )}
              >
                <Users className="w-3 h-3" />
                All Clients
              </button>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="p-2 border-b border-white/10">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={viewMode === 'all' ? "Search all client accounts..." : "Search your accounts..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary/50"
              data-testid="account-search-input"
            />
          </div>
        </div>
        
        {/* Account List */}
        <div className="max-h-[300px] overflow-y-auto py-1">
          {switching ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <span className="ml-2 text-sm text-gray-400">Switching...</span>
            </div>
          ) : filteredAccounts.length === 0 ? (
            <div className="px-3 py-6 text-center text-gray-500 text-sm">
              {searchQuery ? 'No accounts found' : (viewMode === 'all' ? 'No client accounts yet' : 'No accounts linked')}
            </div>
          ) : (
            filteredAccounts.map((account) => (
              <DropdownMenuItem
                key={account.id}
                onClick={() => handleSwitchAccount(account)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors",
                  account.is_active ? "bg-primary/10" : "hover:bg-white/5"
                )}
                data-testid={`account-option-${account.id}`}
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500/30 to-blue-500/30 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-cyan-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {account.business_name || account.name}
                  </p>
                  <div className="flex items-center gap-2">
                    {account.location && (
                      <p className="text-xs text-gray-400 truncate">{account.location}</p>
                    )}
                    {account.crm_connected && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400">CRM</span>
                    )}
                  </div>
                  {/* Show owner info for admin view */}
                  {viewMode === 'all' && account.owner_name && (
                    <p className="text-[10px] text-gray-500 truncate">
                      Owner: {account.owner_name}
                    </p>
                  )}
                </div>
                {account.is_active && (
                  <Check className="w-4 h-4 text-primary flex-shrink-0" />
                )}
              </DropdownMenuItem>
            ))
          )}
        </div>
        
        {/* Add Account Option - Admin only */}
        {isAdmin && (
          <>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem
              onClick={() => {
                setIsOpen(false);
                toast.info('Navigate to Admin > Team to add and manage client accounts');
              }}
              className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-white/5 text-gray-400"
              data-testid="add-account-btn"
            >
              <Plus className="w-4 h-4" />
              <span className="text-sm">Add / Manage Accounts</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default AccountSwitcher;
